// src/SettingsSidebar.js
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { SidebarItem, Group } from "./components/nav/SidebarPrimitives.jsx";
import { useCapabilities } from "./authz/useCapabilities";
import { CAPS } from "./authz/capabilities";
import { useDetectedSourcesCount } from "./hooks/useDetectedSourcesCount";
import { useLastRunFailureCount } from "./hooks/useLastRunFailureCount";
import { getLastAppPath } from "./lib/lastAppPath";

// Route → highlighted settings item. Ordered startsWith checks (most specific first).
export const getActiveSettingsItem = (location) => {
  const p = location.pathname;
  if (p === "/allocation" || p.startsWith("/allocation/") || p === "/detected-sources") return "Cost Sources";
  if (p.startsWith("/cost-allocation/hierarchy") || p.startsWith("/virtual-tags")) return "Allocation Hierarchy";
  if (p.startsWith("/cost-source-taxonomy")) return "Taxonomies";
  if (p.startsWith("/source-connections")) return "Data Connections";
  if (p.startsWith("/data-pipeline") || p.startsWith("/reporting-jobs")) return "Data Pipeline";
  if (p.startsWith("/invoice-inbound-tokens")) return "Invoice Ingestion";
  if (p.startsWith("/settings/ai-subscription")) return "AI Subscription Plan";
  if (p.startsWith("/settings/users") || p.startsWith("/litellm-ingest-tokens")) return "Users & Access";
  if (p.startsWith("/settings/roles")) return "Roles & Access";
  if (p.startsWith("/settings/service-accounts")) return "Service accounts";
  if (p.startsWith("/settings/access-review")) return "Access review";
  if (p.startsWith("/settings/audit-evidence")) return "Audit & evidence";
  if (p.startsWith("/provider-mappings") || p.startsWith("/application-parameters")) return "System Configuration";
  return "";
};

const SettingsSidebar = () => {
  const [isOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const detectedCount = useDetectedSourcesCount();
  const failureCount = useLastRunFailureCount();
  const { capabilities } = useCapabilities();
  const can = (c) => capabilities.includes(c);
  // Per-item capability gates, matching the backend route each destination needs
  // (see backend_go/cmd/api/routes.go): integrations surfaces share CapIntegrationsManage,
  // the AI subscription plan write needs CapConfigAllocModels, taxonomy writes need
  // CapConfigDimensions, and the access/system group splits across user vs. tenant config.
  const canDataConnections = can(CAPS.INTEGRATIONS_MANAGE);
  const canDataPipeline = can(CAPS.INTEGRATIONS_MANAGE);
  const canInvoiceIngestion = can(CAPS.INTEGRATIONS_MANAGE);
  const canAiSubscription = can(CAPS.CONFIG_ALLOC_MODELS);
  const canTaxonomies = can(CAPS.CONFIG_DIMENSIONS);
  const canUsersAccess = can(CAPS.USERS_MANAGE) || can(CAPS.ROLES_ASSIGN);
  const canRolesAccess = can(CAPS.ROLES_ASSIGN);
  const canServiceAccounts = can(CAPS.SERVICE_ACCOUNTS_MANAGE);
  const canAccessReview = can(CAPS.ACCESS_REVIEW);
  const canAuditExport = can(CAPS.AUDIT_EXPORT);
  const canSystemConfig = can(CAPS.CONFIG_DIMENSIONS) || can(CAPS.TENANT_CONFIGURE);

  const [activeTab, setActiveTab] = useState(getActiveSettingsItem(location));
  useEffect(() => setActiveTab(getActiveSettingsItem(location)), [location]);

  const itemProps = { isOpen, activeTab, setActiveTab };

  return (
    <div
      style={{ height: "100vh" }}
      className="flex-none flex flex-col bg-side sticky top-0 sidebar min-w-[288px] text-left"
    >
      {/* Exit-to-app control replaces the brand row */}
      <div className="flex-none px-6 pt-6 pb-4">
        <button
          type="button"
          onClick={() => navigate(getLastAppPath())}
          className="flex items-center gap-2 text-sm font-semibold text-text-2 hover:text-text transition-colors"
        >
          <FontAwesomeIcon icon={icon({ name: "arrow-left", family: "sharp", style: "light" })} className="text-[15px]" />
          Exit to app
        </button>
        {/* Settings context label (brand wordmark removed). */}
        <div className="mt-4 px-1">
          <div className="text-[20px] font-extrabold tracking-[-0.015em] text-text font-['Plus_Jakarta_Sans',sans-serif]">Settings</div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pb-4">
        <Group label="Financial model" isOpen={isOpen}>
          <SidebarItem url="/allocation" {...itemProps} tabName="Cost Sources"
            sidebarIcon={icon({ name: "database", family: "sharp", style: "light" })}
            tooltip="Cost Sources" tooltipDescription="Cost sources, detected sources and naming rules"
            badge={detectedCount} />
          <SidebarItem url="/cost-allocation/hierarchy" {...itemProps} tabName="Allocation Hierarchy"
            sidebarIcon={icon({ name: "sitemap", family: "sharp", style: "light" })}
            tooltip="Allocation Hierarchy" tooltipDescription="Configure the cost allocation hierarchy and virtual tags" />
          {canTaxonomies && (
            <SidebarItem url="/cost-source-taxonomy" {...itemProps} tabName="Taxonomies"
              sidebarIcon={icon({ name: "folder-tree", family: "sharp", style: "light" })}
              tooltip="Taxonomies" tooltipDescription="Cost source taxonomy and categorization" />
          )}
        </Group>

        {(canDataConnections || canDataPipeline || canInvoiceIngestion || canAiSubscription) && (
          <Group label="Data & integrations" isOpen={isOpen}>
            {canDataConnections && (
              <SidebarItem url="/source-connections" {...itemProps} tabName="Data Connections"
                sidebarIcon={icon({ name: "plug", family: "sharp", style: "light" })}
                tooltip="Data Connections" tooltipDescription="Manage data source credentials and import health" />
            )}
            {canDataPipeline && (
              <SidebarItem url="/data-pipeline" {...itemProps} tabName="Data Pipeline"
                sidebarIcon={icon({ name: "diagram-project", family: "sharp", style: "light" })}
                tooltip="Data Pipeline" tooltipDescription="Data pipeline, reporting jobs and activity log"
                badge={failureCount} />
            )}
            {canInvoiceIngestion && (
              <SidebarItem url="/invoice-inbound-tokens" {...itemProps} tabName="Invoice Ingestion"
                sidebarIcon={icon({ name: "key", family: "sharp", style: "light" })}
                tooltip="Invoice ingestion" tooltipDescription="Manage inbound tokens for automated invoice ingestion" />
            )}
            {canAiSubscription && (
              <SidebarItem url="/settings/ai-subscription" {...itemProps} tabName="AI Subscription Plan"
                sidebarIcon={icon({ name: "credit-card", family: "sharp", style: "light" })}
                tooltip="AI subscription plan" tooltipDescription="Configure the flat-fee AI subscription plan used to re-base AI cost" />
            )}
          </Group>
        )}

        {(canUsersAccess || canRolesAccess || canServiceAccounts || canAccessReview || canAuditExport || canSystemConfig) && (
          <Group label="Access & system" isOpen={isOpen}>
            {canUsersAccess && (
              <SidebarItem url="/settings/users" {...itemProps} tabName="Users & Access"
                sidebarIcon={icon({ name: "users", family: "sharp", style: "light" })}
                tooltip="Users & Access" tooltipDescription="Manage users, roles and ingest tokens" />
            )}
            {canRolesAccess && (
              <SidebarItem url="/settings/roles" {...itemProps} tabName="Roles & Access"
                sidebarIcon={icon({ name: "shield-halved", family: "sharp", style: "light" })}
                tooltip="Roles & Access" tooltipDescription="Role catalog: capabilities, risk and conflicts" />
            )}
            {canServiceAccounts && (
              <SidebarItem url="/settings/service-accounts" {...itemProps} tabName="Service accounts"
                sidebarIcon={icon({ name: "robot", family: "sharp", style: "light" })}
                tooltip="Service accounts" tooltipDescription="Machine-to-machine principals with a restricted capability set" />
            )}
            {canAccessReview && (
              <SidebarItem url="/settings/access-review" {...itemProps} tabName="Access review"
                sidebarIcon={icon({ name: "clipboard-check", family: "sharp", style: "light" })}
                tooltip="Access review" tooltipDescription="Periodic review of who holds tenant-owner access" />
            )}
            {canAuditExport && (
              <SidebarItem url="/settings/audit-evidence" {...itemProps} tabName="Audit & evidence"
                sidebarIcon={icon({ name: "file-shield", family: "sharp", style: "light" })}
                tooltip="Audit & evidence" tooltipDescription="Audit log + effective-access evidence export" />
            )}
            {canSystemConfig && (
              <SidebarItem url="/provider-mappings" {...itemProps} tabName="System Configuration"
                sidebarIcon={icon({ name: "gears", family: "sharp", style: "light" })}
                tooltip="System Configuration" tooltipDescription="Provider mappings, application parameters and maintenance" />
            )}
          </Group>
        )}
      </div>
    </div>
  );
};

export default SettingsSidebar;
